import { Flex, Text } from '@chakra-ui/react';
import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button } from '@/components';

interface PaginationProps {
  total?: number;
  pageSize?: number;
  pageIndex?: number;
  onPageChange?: (page: number) => void;
}

const BasePagination: React.FC<PaginationProps> = ({
  total = 0,
  pageSize = 10,
  pageIndex = 1,
  onPageChange,
}) => {
  const totalPages = !total ? 0 : Math.ceil(total / pageSize);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange?.(page);
    }
  };

  const generatePages = () => {
    const pages: (number | '...')[] = [];
    const visiblePages = 5;
    if (totalPages <= visiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (pageIndex > 3) pages.push('...');

      const startPage = Math.max(2, pageIndex - 1);
      const endPage = Math.min(totalPages - 1, pageIndex + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (pageIndex < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Flex className="base-paging" align="center" justify="center" mt={4} gap={2}>
      <Button
        type="button"
        onClick={() => handlePageChange(pageIndex - 1)}
        disabled={pageIndex === 1}
        variant="outline"
      >
        <FiChevronLeft />
      </Button>

      {generatePages().map((page, idx) => (
        <React.Fragment key={idx}>
          {page === '...' ? (
            <Text as="span" px={2}>
              ...
            </Text>
          ) : (
            <Button
              className={page === pageIndex ? 'active' : ''}
              onClick={() => handlePageChange(page as number)}
              colorScheme={page === pageIndex ? 'teal' : 'gray'}
              variant={page === pageIndex ? 'ghost' : 'outline'}
            >
              {page}
            </Button>
          )}
        </React.Fragment>
      ))}
      <Button
        type="button"
        aria-label="Next Page"
        onClick={() => handlePageChange(pageIndex + 1)}
        disabled={pageIndex === totalPages}
        variant="outline"
      >
        <FiChevronRight />
      </Button>
    </Flex>
  );
};

export { BasePagination };
